import { useEffect, useState } from 'react'
import { Smartphone } from 'lucide-react'
import type { NativeAppDef } from '@/lib/sandboxProjects'

type Props = { app: NativeAppDef; lang: 'de' | 'en'; title: string }

/**
 * A native app as tappable snapshots: real screens captured from the demo
 * build, with the tab bar and key rows wired so a visitor can walk through
 * it. Tapping anywhere else briefly reveals where the taps are.
 */
export default function NativeFrame({ app, lang, title }: Props) {
  const de = lang === 'de'
  const snaps = app.snapshots
  const [screen, setScreen] = useState(snaps?.start ?? '')
  const [hint, setHint] = useState(false)

  useEffect(() => {
    if (!hint) return
    const t = setTimeout(() => setHint(false), 1400)
    return () => clearTimeout(t)
  }, [hint])

  const os = app.kind === 'ios' ? 'iOS' : 'Android'
  const current = snaps?.screens[screen]

  return (
    <div className={`sb-native ${current ? '' : 'sb-native--missing'}`}>
      <div className={`sb-phone ${app.kind === 'android' ? 'sb-phone--android' : ''} ${current ? 'sb-phone--shot' : ''}`} role="group" aria-label={title}>
        {app.kind === 'ios' && !current && <div className="sb-phone__notch" />}
        {current ? (
          <div className={`sb-phone__screen sb-phone__screen--shot ${hint ? 'is-hinting' : ''}`} onClick={() => setHint(true)}>
            <img src={current.image} alt={`${title} — ${screen}`} draggable={false} />
            {current.hotspots.map((h) => (
              <button
                key={`${h.to}-${h.x}-${h.y}`}
                type="button"
                className="sb-hotspot"
                style={{ left: `${h.x}%`, top: `${h.y}%`, width: `${h.w}%`, height: `${h.h}%` }}
                aria-label={h.label}
                title={h.label}
                onClick={(e) => {
                  e.stopPropagation()
                  setScreen(h.to)
                }}
              />
            ))}
          </div>
        ) : (
          <div className="sb-phone__screen">
            <Smartphone size={28} strokeWidth={1.5} />
            <p className="sb-phone__title">{title}</p>
            <p className="sb-phone__text">
              {de
                ? `Die Screens der ${os}-App werden gerade aufgenommen.`
                : `Screens of the ${os} app are being captured.`}
            </p>
          </div>
        )}
      </div>
      {current && (
        <p className="mono muted sb-native__hint">
          {de
            ? 'Echte Screens der App — Tabs und markierte Zeilen sind antippbar.'
            : 'Real screens of the app — tabs and highlighted rows are tappable.'}
        </p>
      )}
    </div>
  )
}
